// ============================================================================
// OrgNodeCard — carte compacte d'une personne dans l'arbre (E1c, F-238)
// ============================================================================
// Portage du PoC (components/org/org-node-card.tsx) au standard maison.
//
// ECARTS ASSUMES (decisions GRAVEES du MASTER — voir rapport E1c) :
//   1. AUCUN LIEN PROFIL (F-238/M-216). Le PoC enveloppait la carte dans un
//      <Link href={node.href}> vers /profile ou /profile/{email}?from=organigramme.
//      Ces routes N'EXISTENT PAS dans gest C-native (surface /profile non portee,
//      hors lot) — un lien mort est pire que pas de lien. La carte est donc un
//      bloc NON cliquable en V1. Fiche backlog au merge : [GEST-PROFILE-SURFACE].
//      Consequence : le toggle de branche redevient le SEUL controle interactif de
//      la carte, donc plus de contrainte « bouton frere du lien » (le PoC evitait
//      le bouton IMBRIQUE dans le lien, qui casse la navigation clavier).
//   2. AVATARS = INITIALES SEULEMENT (decision 2). Le blob Firebase n'est pas migre
//      et google_avatar_url est une URL externe (GEST-STORAGE-MIGRATION) : aucune
//      tentative de servir une image. getInitials est deja porte (lib/profile).
// Regle #28 : zero CSS dur — tokens Tailwind du parc, cards blanches (jamais de
// card coloree), rounded-xl, shadow-sm.

import { ChevronDown, ChevronUp } from 'lucide-react';
import type { OrgNode } from '../../lib/org/hierarchy';
import { getInitials } from '../../lib/profile/initials';
import { useTranslation } from '../../i18n';

/** Pastille d'initiales — remplace l'avatar image (decision gravee 2). */
function OrgInitials({ node, isRoot }: { node: OrgNode; isRoot: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-slate-100 font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200 ${
        isRoot ? 'h-12 w-12 text-field' : 'h-10 w-10 text-chip'
      } ${node.is_team_director || isRoot ? 'ring-2 ring-brand-500/40' : ''}`}
    >
      {getInitials(node.full_name, node.email)}
    </span>
  );
}

export function OrgNodeCard({
  node,
  depth,
  isCollapsed,
  isHighlighted,
  onToggle,
}: {
  node: OrgNode;
  depth: number;
  isCollapsed: boolean;
  isHighlighted?: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const isRoot = depth === 0;
  const displayName = node.full_name ?? node.email;

  return (
    // `id` ciblé par le centrage recherche (zoomToElement). Le surlignage est un
    // anneau accentue autour de la carte trouvee (F-234).
    <div
      id={`org-node-${node.email}`}
      className={`relative rounded-xl transition-shadow ${
        isHighlighted ? 'ring-2 ring-brand-500 ring-offset-2 ring-offset-transparent' : ''
      }`}
    >
      <div
        className={`flex flex-col items-center gap-1.5 rounded-xl border bg-white p-3 text-center shadow-sm dark:bg-slate-800 ${
          isRoot ? 'w-52' : 'w-44'
        } ${
          node.is_team_director && !isRoot
            ? 'border-brand-200 dark:border-brand-900'
            : 'border-slate-200 dark:border-slate-700'
        }`}
      >
        <OrgInitials node={node} isRoot={isRoot} />
        <span
          className={`w-full truncate font-medium text-slate-900 dark:text-slate-100 ${
            isRoot ? 'text-field' : 'text-chip'
          }`}
          title={displayName}
        >
          {displayName}
        </span>
        {isRoot ? (
          <span aria-hidden="true" className="h-0.5 w-10 rounded-full bg-brand-500" />
        ) : null}
        {node.job_title ? (
          <span
            className="w-full truncate text-chip text-slate-500 dark:text-slate-400"
            title={node.job_title}
          >
            {node.job_title}
          </span>
        ) : null}
        {/* L'equipe n'est repetee sur la carte que si le noeud n'est PAS deja sous
            une boite d'etiquette d'equipe (groupe non etiquete = meme equipe que le
            parent) — sinon l'information serait dite deux fois. */}
        {!isRoot && node.is_team_director ? (
          <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-chip font-medium uppercase text-brand-700 dark:bg-brand-950/40 dark:text-brand-300">
            {t('gest:organigramme.node.director')}
          </span>
        ) : null}
      </div>

      {node.descendant_count > 0 ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!isCollapsed}
          aria-label={
            isCollapsed
              ? t('gest:organigramme.node.expandBranch', { name: displayName, count: node.descendant_count })
              : t('gest:organigramme.node.collapseBranch', { name: displayName })
          }
          className="absolute -bottom-3 left-1/2 z-10 inline-flex h-6 min-w-6 -translate-x-1/2 touch-manipulation items-center justify-center gap-0.5 rounded-full border border-slate-200 bg-white px-1.5 text-chip tabular-nums text-slate-500 shadow-sm transition-colors hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
        >
          {isCollapsed ? (
            <>
              <ChevronDown size={14} aria-hidden="true" />
              {node.descendant_count}
            </>
          ) : (
            <ChevronUp size={14} aria-hidden="true" />
          )}
        </button>
      ) : null}
    </div>
  );
}
